import { useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, VideoOff } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ClipPlayer, READER_FRAME, SHOT_ASPECT } from '../ClipPlayer';
import { ReaderScale } from '../ReaderScale';
import { CaptionText } from '../CaptionText';
import { captionAt } from '../assemblyClip';
import { publicMeshUrl } from '../publicUrl';
import { tutorialFinished, tutorialScreens, type PublicTutorialData } from './PublicTutorial';
import { ScreenProgress } from './PublicScreen';
import { TutorialDone } from './TutorialDone';

// THE PICTURE'S BOX: AS TALL AS THE WINDOW LEAVES IT, as wide as the column. It was a 16:9 strip
// 672 px wide in the middle of whatever screen it was on — a third of a desktop window, and a
// letterbox across a phone held upright, around the one thing the page is for.
// AT ONE SHAPE (SHOT_ASPECT): as wide as the room it stands in or as that room's height allows at
// 9:16, whichever is less, so a phone wider than that gets a band either side, never a different
// picture. The room is MEASURED (`cqw`/`cqh`, see ROOM): it was the window less a guessed 9rem,
// when the count above takes 5, and the 4 left over were bands above and below the picture that
// cost it 36 px of width on an iPhone 13 (Louis 2026-10-28). A floor for a window too short to
// leave anything.
const FRAME = `${SHOT_ASPECT} w-[min(100cqw,calc(100cqh*9/16))] min-w-[8.4375rem] ${READER_FRAME}`;
// WHAT IS LEFT UNDER THE COUNT, the box FRAME is measured against: it fills the column's height
// and contributes none of its own (size containment), so the page is the window's height and no more.
const ROOM = 'flex min-h-0 flex-1 items-center justify-center [container-type:size]';
// THE SPEEDS THE READER'S BUTTON OFFERS, beside the ring (see ClipPlayer's `speeds`).
const PUBLIC_SPEEDS = [1, 2];

// The step-by-step player: the tutorial's sub-steps are flattened into an ordered sequence
// of screens (a frame and its instruction each), and the visitor moves forward with
// "Suivant" / "Next". Reached from the splash via "Ready".
// `onExit` returns to the splash, used by "Retour" on the very first screen.
//
// A FRAME PLAYS ITS SUB-STEP where the manual's owner signed one off, and is empty where
// nobody has. What plays is not a video: the publish snapshot carries the movement — which
// part goes where, in along which axis, how far — and this page computes every frame of it
// from those numbers, with the meshes downloaded from the tutorial's own gated route. So the
// clip a visitor watches is the one that was approved, the same code drawing it.
export function TutorialPlayer({
  docId,
  data,
  idx,
  onIdx,
  onExit,
  onHome,
  startedAt,
  brightness,
  variant = '',
}: {
  // THE COLOURWAY THE VISITOR PICKED on the splash: its meshes are drawn in its colours (see
  // publicMeshUrl), and its name is under the heading.
  variant?: string;
  // How bright the picture is: one setting, held by the page (see PublicTutorial).
  brightness: number;
  // Whose meshes these are. A clip names its parts by Parts DB run and never by address —
  // the address is gated on THIS tutorial being published, which only the page knows.
  docId: string;
  data: PublicTutorialData;
  // WHICH SCREEN, HELD BY THE PAGE AND NOT BY THIS. It was a `useState` in here, which is a
  // place a position cannot survive a reload from — and a visitor with a phone propped against
  // a chair leg reloads for every reason there is. The page keeps it in the address (see
  // PublicTutorial), so refreshing lands back on the screen that was open rather than at the
  // splash with the whole tutorial to walk again.
  idx: number;
  onIdx: (next: number) => void;
  onExit?: () => void;
  // The end screen's one button, back to the splash.
  onHome: () => void;
  // When this tutorial was first opened in this tab, which the end screen times from.
  startedAt: number | null;
}) {
  const { t, language } = useLanguage();

  const screens = useMemo(() => tutorialScreens(data), [data]);
  // AND ONE MORE, AFTER THE LAST GESTURE: the product standing assembled, with a word that it is
  // done and no instruction — what the last clip leaves, at rest (see finishedClip). Absent where
  // no screen has a clip, which is a tutorial with no picture of anything.
  const finished = useMemo(() => tutorialFinished(screens), [screens]);
  // Stable, so moving between screens does not look like a new mesh address to the clip
  // player and restart a download that has already landed.
  const meshUrl = useCallback(
    (job: number) => publicMeshUrl(docId, job, variant),
    [docId, variant],
  );


  const back = () => (idx <= 0 ? onExit?.() : onIdx(Math.max(0, idx - 1)));
  // On past the last step is the end screen (see TutorialDone).
  const next = () => onIdx(Math.min(screens.length, idx + 1));

  // `at`: this screen's place, for the header the tools and parts screens have too — back on the
  // left, "01 / 06" and its bar — at the same place as theirs (Louis 2026-10-23). Out of the
  // steps alone: the finished product after them is not one, and shows the last, "10 / 10".
  // A FUNCTION AND NOT A COMPONENT: defined in here, a component is a new type at every render, and
  // React threw away and rebuilt all it held each time, the 3D picture reloading and the end
  // screen sending its time twice (Louis 2026-10-23).
  // `room`: the screen holds ROOM, which needs a height that is DEFINITE, the window's (h-dvh). The
  // page around it is only at least that tall (min-h-dvh), and Chrome then measures ROOM's height
  // as nothing: the picture fell to its 135 px floor on a desktop (Louis 2026-10-29).
  const frame = (at: number | undefined, children: React.ReactNode, room = false) => (
    <div className={`flex ${room ? 'h-dvh' : 'flex-1'} flex-col bg-white font-satoshi text-slate-900`}>
      {at != null && (
        <div className="mx-auto w-full max-w-md px-5 pt-2">
          <div className="flex h-10 items-center justify-center">
            <ScreenProgress at={at} of={screens.length} onBack={back} onPick={(n) => onIdx(n - 1)} />
          </div>
        </div>
      )}
      {/* THE WHOLE SCREEN IS ONE BLOCK, CENTRED — title, picture, sentence, buttons, in that
          order and with nothing stretched between them.
          It used to stretch: the column filled the window, the buttons sat on the bottom
          edge, and whatever was told to absorb the difference absorbed all of it. On a phone
          that is invisible, because there is no difference to absorb; on a desktop window it
          put three hundred pixels of nothing between the heading and the picture and as much
          again under the sentence. Centring the block instead puts the same empty space
          outside it, where it reads as margin rather than as a gap between two things that
          belong together.
          NO TITLE OVER IT: the product's name and colourway are the splash's, said once before
          the first screen, and repeated over every one they were a banner between the reader
          and the count that says where they are. */}
      <div className={`mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-4 pb-6 ${at != null ? 'pt-2' : 'pt-8'}`}>
        {children}
      </div>
    </div>
  );

  if (screens.length === 0) {
    return (
      frame(undefined, <>
        <p className="text-center text-sm text-slate-500">{t('notice.public.empty')}</p>
      </>)
    );
  }

  // THE WAY THROUGH, ON THE PICTURE: a round arrow either side of the sentence (see Caption). It
  // was a row of two wide bars under the transport, "Previous" and "Next", which took a band of
  // a phone's screen for two words; on the picture's own edge the arrows cost it nothing, and
  // they sit where the eye already is, on the sentence it has just read.
  // THE END, after the last step (see TutorialDone).
  if (idx >= screens.length) {
    return (
      frame(screens.length, <>
        <TutorialDone
          docId={docId}
          startedAt={startedAt}
          finished={finished}
          meshUrl={meshUrl}
          brightness={brightness}
          onHome={onHome}
        />
      </>)
    );
  }

  const cur = screens[Math.min(idx, screens.length - 1)];
  const isLast = idx >= screens.length - 1;
  // The sub-step instruction in the toggled language (falling back to the other).
  const instruction =
    (language === 'fr' ? cur.sub.textFr : cur.sub.textEn) || cur.sub.textEn || cur.sub.textFr;

  // THE BEAT BEING PLAYED, where the screen has a clip and Phase 4 cut the sentence (see captionAt).
  const beats = language === 'fr' ? cur.sub.beatsFr : cur.sub.beatsEn;
  const caption = (ring?: React.ReactNode, at = 0) => {
    const beat = cur.sub.clip ? captionAt(beats, instruction, cur.sub.clip.movement, at) : undefined;
    return <Caption text={beat?.text ?? instruction} beat={beat} onBack={back} onNext={next} last={isLast} lead={ring} />;
  };

  return (
    frame(Math.min(idx, screens.length - 1) + 1, <>
      <div className={ROOM}>
        {/* WHERE THIS SCREEN IS: in the header (see frame). It was "STEP 1 - SUB-STEP 1-1", the
            manual's own address, then a count alone over the picture. */}
        {/* THE SCREEN'S OWN CONTENT, CENTRED IN WHAT IS LEFT between the heading above it and
            the buttons below. The frame used to sit hard under the heading with the
            instruction floating in the middle of the space beneath it — because the
            instruction was the element told to fill that space, and filling it is what put
            the picture at the top of a phone-shaped page and a hand's width of nothing under
            it.
            The two travel together: the sentence is about the picture, and a gap between them
            wide enough to read as a section break is a gap that says they are two things. */}
        {/* THIS SCREEN'S ACTION, playing — or the frame it would play in, where the manual
            has no clip for it. The same box either way: a tutorial is a sequence of screens
            of one shape, and a step nobody animated is a quieter screen rather than a
            different page. */}
        {cur.sub.clip ? (
          <ClipPlayer
            clip={cur.sub.clip}
            label={cur.name}
            meshUrl={meshUrl}
            className={FRAME}
            brightness={brightness}
            // THE FULL TRANSPORT, on the screen somebody is assembling from. A visitor
            // following a manual wants to stop on the frame where the two parts meet and
            // look at it — which is the one thing a play button cannot do.
            transport
            // AND IT WAITS TO BE ASKED. A thumbnail in a list plays itself because it is
            // there to be recognised; this is an instruction, and it opens on the frame
            // BEFORE the action — the parts as they are in the visitor's hands right now.
            // Playing on arrival shows them the move once, while they are still reading the
            // sentence, and then holds the finished assembly, which is the least useful
            // frame of the lot.
            autoPlay={false}
            // AND IT STOPS WHERE IT ENDS: the parts together, held, until play is pressed again —
            // a movement starting over on its own is one a visitor has to catch at the right moment.
            once
            // ×1 OR ×2, beside the ring: a reader who has the move after one viewing.
            speeds={PUBLIC_SPEEDS}
            // THE FRAME IS THE PAGE'S WIDTH ALREADY: no bigger view of it to open.
            noBigView
            caption={caption}
          />
        ) : (
          <div className={`relative flex flex-col items-center justify-center gap-2 text-notice-blue/50 ${FRAME}`}>
            <VideoOff className="h-10 w-10" strokeWidth={1.25} />
            <span className="text-sm font-medium">{t('notice.public.noVideo')}</span>
            <ReaderScale>{caption()}</ReaderScale>
          </div>
        )}
        {/* A BADGE SAYING WHERE THEY WERE IN THE REPEAT — "Cycle 1 (2/2)" — used to sit
            here, last before "Next", answering "how many more times am I doing this?".
            There are no repeats: every gesture the manual makes has a screen of its own. */}
      </div>

    </>, true)
  );
}

// WHAT TO DO, WRITTEN ON THE PICTURE OF IT — a bubble along the bottom of the frame. It sat
// under the transport, a paragraph below the thing it describes: on a phone propped against a
// chair leg the eye is on the picture, and the sentence was a scroll away from it.
//
// IT TAKES NO PRESS: a drag that starts on it still turns the picture underneath.
//
// THE SENTENCE ITSELF IS CaptionText (see it).
// Also Phase 6's, under the picture it frames (see AssemblyPhase).
export function Caption({ text, beat, onBack, onNext, last, lead }: {
  text: string;
  beat?: { index: number; count: number };
  // THE ROUND TRANSPORT and its speed, above the row at the left, where the screen has a clip.
  lead?: React.ReactNode;
  onBack: () => void;
  onNext: () => void;
  // The last screen's arrow finishes the tutorial rather than going on.
  last: boolean;
}) {
  const { t } = useLanguage();
  // BOTH ARROWS ALIKE, white with the blue edge: which way is on is said by the chevron, and a
  // filled one beside a hollow one read as a primary action and a lesser one.
  // THE HOVER OPAQUE: the brand blue at 5% laid over white, worked out once. As a translucent
  // tint it replaced the white, and over dark wood the arrow went see-through (Louis 2026-10-28).
  const arrow = 'pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-notice-blue bg-white text-notice-blue shadow-sm transition-colors hover:bg-[#f4f7fa]';
  return (
    // AT THE BOTTOM OF THE PICTURE: the sentence, an arrow either side of it and level with its
    // middle, 6px from the picture's edges and always 8px from the sentence, which fills the width
    // between them; the play control on its own above, at the left (Louis 2026-10-23).
    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 px-1.5 pb-2">
      {lead}
      <div className="flex w-full items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('notice.public.back')}
          className={arrow}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="relative min-w-0 flex-1">
          {/* "2/2", WHICH OF THE STEP'S SENTENCES THIS IS: small, just over the sentence's middle,
              out of its bubble, where it took a sixth of the first line, and out of the arrows'
              row, so they stay level with the sentence. */}
          {beat && beat.count > 1 && (
            <span className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded-full bg-white/95 px-1.5 text-[11px] font-semibold leading-4 tabular-nums text-notice-blue/70 shadow-sm">
              {`${beat.index + 1}/${beat.count}`}
            </span>
          )}
          {text && <CaptionText text={text} fill />}
        </div>
        <button
          type="button"
          onClick={onNext}
          aria-label={t(last ? 'notice.public.finish' : 'notice.public.next')}
          className={arrow}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
